export const FLOOR_INDEX_SCHEMA = 'csgo2-radar/floor-index/v1';

export function normalizedLayers(layers = []) {
  return layers
    .map((layer, index) => ({
      ...layer,
      id: String(layer.id || `layer-${index + 1}`),
      displayName: String(layer.displayName || layer.id || `楼层 ${index + 1}`),
      zMin: Number(layer.zMin),
      zMax: Number(layer.zMax),
      order: Number.isFinite(Number(layer.order)) ? Number(layer.order) : index,
    }))
    .filter((layer) => Number.isFinite(layer.zMin) && Number.isFinite(layer.zMax) && layer.zMin < layer.zMax)
    .sort((a, b) => a.order - b.order || a.zMin - b.zMin);
}

function intervalDistance(z, layer) {
  if (z < layer.zMin) return layer.zMin - z;
  if (z > layer.zMax) return z - layer.zMax;
  return 0;
}

export function selectLayerForHeight(layers, sourceZ, currentLayer = null, hysteresis = 24) {
  const available = normalizedLayers(layers);
  if (!available.length || !Number.isFinite(Number(sourceZ))) return null;
  const z = Number(sourceZ);
  const padding = Math.max(0, Number(hysteresis) || 0);
  const current = currentLayer && available.find((layer) => layer.id === currentLayer.id);

  if (current && z >= current.zMin - padding && z <= current.zMax + padding) {
    return {
      layer: current,
      reason: z >= current.zMin && z <= current.zMax ? 'inside' : 'hysteresis',
      distance: intervalDistance(z, current),
    };
  }

  const containing = available.find((layer) => z >= layer.zMin && z <= layer.zMax);
  if (containing) return { layer: containing, reason: 'inside', distance: 0 };

  const nearest = available.reduce((best, layer) => {
    const distance = intervalDistance(z, layer);
    return !best || distance < best.distance ? { layer, distance } : best;
  }, null);
  return nearest ? { ...nearest, reason: 'nearest' } : null;
}

export function computeFloorCoverage(layers, geometryMin, geometryMax) {
  const minimum = Number(geometryMin);
  const maximum = Number(geometryMax);
  const available = normalizedLayers(layers);
  if (!Number.isFinite(minimum) || !Number.isFinite(maximum) || minimum >= maximum) {
    return { coveredUnits: 0, totalUnits: 0, coverageRatio: 0, unmappedRanges: [] };
  }

  const intervals = available
    .map((layer) => [Math.max(minimum, layer.zMin), Math.min(maximum, layer.zMax)])
    .filter(([start, end]) => start < end)
    .sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const interval of intervals) {
    const previous = merged.at(-1);
    if (!previous || interval[0] > previous[1]) merged.push([...interval]);
    else previous[1] = Math.max(previous[1], interval[1]);
  }

  const unmappedRanges = [];
  let cursor = minimum;
  for (const [start, end] of merged) {
    if (cursor < start) unmappedRanges.push({ zMin: cursor, zMax: start });
    cursor = Math.max(cursor, end);
  }
  if (cursor < maximum) unmappedRanges.push({ zMin: cursor, zMax: maximum });
  const coveredUnits = merged.reduce((sum, [start, end]) => sum + end - start, 0);
  const totalUnits = maximum - minimum;
  return {
    coveredUnits,
    totalUnits,
    coverageRatio: totalUnits > 0 ? coveredUnits / totalUnits : 0,
    unmappedRanges,
  };
}

export function validateFloorIndex(index) {
  if (index?.schema !== FLOOR_INDEX_SCHEMA || index?.gameId !== 'csgo2') {
    throw new Error('CS2RADAR-FLOOR-001: invalid floor index');
  }
  const layers = normalizedLayers(index.layers);
  if (!layers.length || layers.length !== index.layers.length) {
    throw new Error('CS2RADAR-FLOOR-002: invalid floor layers');
  }
  return index;
}
